import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import ScrollToTop from "./components/ScrollToTop";

// Homepage stays eager for LCP — no react-query / framer / supabase on this path
import Index from "./pages/Index";

const QueryProvider = lazy(() => import("./providers/QueryProvider"));

const EpisodePage = lazy(() => import("./pages/EpisodePage"));
const EpisodesPage = lazy(() => import("./pages/EpisodesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SponsorshipPage = lazy(() => import("./pages/SponsorshipPage"));
const Login = lazy(() => import("./pages/admin/Login"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const EpisodeEditor = lazy(() => import("./pages/admin/EpisodeEditor"));
const SocialLinksManager = lazy(() => import("./pages/admin/SocialLinksManager"));
const PhoneAgent = lazy(() => import("./pages/admin/PhoneAgent"));
const SocialPage = lazy(() => import("./pages/SocialPage"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const GuestConsentForm = lazy(() => import("./pages/GuestConsentForm"));
const LocationConsentForm = lazy(() => import("./pages/LocationConsentForm"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const MiamiEvent = lazy(() => import("./pages/MiamiEvent"));
const MiamiEventSuccess = lazy(() => import("./pages/MiamiEventSuccess"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const GuestInquiryPage = lazy(() => import("./pages/GuestInquiryPage"));
const PastGuestsPage = lazy(() => import("./pages/PastGuestsPage"));
const Vision2026Page = lazy(() => import("./pages/Vision2026Page"));
const TellYourStoryPage = lazy(() => import("./pages/TellYourStoryPage"));
const BeAGuestSEOPage = lazy(() => import("./pages/BeAGuestSEOPage"));

const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

const RouteFallback = () => (
  <div className="min-h-screen bg-background" aria-busy="true" aria-live="polite" />
);

/**
 * Mount non-critical UI chrome after real user input, or a long fallback.
 * Avoid requestIdleCallback — Lighthouse fires idle early and then Sonner/toasts
 * measure layout (forced reflow) during the lab window.
 */
function DeferredChrome({
  children,
  fallbackMs = 12000,
}: {
  children: ReactNode;
  fallbackMs?: number;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let enabled = false;

    const enable = () => {
      if (enabled) return;
      enabled = true;
      setReady(true);
    };

    window.addEventListener("pointerdown", enable, { once: true, passive: true });
    window.addEventListener("keydown", enable, { once: true });
    window.addEventListener("touchstart", enable, { once: true, passive: true });
    timeoutId = setTimeout(enable, fallbackMs);

    return () => {
      window.removeEventListener("pointerdown", enable);
      window.removeEventListener("keydown", enable);
      window.removeEventListener("touchstart", enable);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [fallbackMs]);

  if (!ready) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
}

const App = () => (
  <AuthProvider>
    <DeferredChrome>
      <Toaster />
      <Sonner />
    </DeferredChrome>
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Homepage: no QueryProvider — data sections bring their own when deferred */}
          <Route path="/" element={<Index />} />

          {/* All other routes share a lazily loaded react-query provider */}
          <Route
            element={
              <Suspense fallback={<RouteFallback />}>
                <QueryProvider />
              </Suspense>
            }
          >
            <Route path="/episodios" element={<EpisodesPage />} />
            <Route path="/episodio/:id" element={<EpisodePage />} />
            <Route path="/contacto" element={<ContactPage />} />
            <Route path="/patrocinios" element={<SponsorshipPage />} />
            <Route path="/tienda" element={<ShopPage />} />
            <Route path="/tienda/producto/:handle" element={<ProductPage />} />
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin" element={<Dashboard />} />
            <Route path="/admin/episodes/:id" element={<EpisodeEditor />} />
            <Route path="/admin/social-links" element={<SocialLinksManager />} />
            <Route path="/admin/phone-agent" element={<PhoneAgent />} />
            <Route path="/social" element={<SocialPage />} />
            <Route path="/suscribirse" element={<Subscribe />} />
            <Route path="/suscripcion-exitosa" element={<SubscriptionSuccess />} />
            <Route path="/consentimiento" element={<GuestConsentForm />} />
            <Route path="/autorizacion-filmacion" element={<LocationConsentForm />} />
            <Route path="/privacidad" element={<PrivacyPolicy />} />
            <Route path="/terminos" element={<TermsOfUse />} />
            <Route path="/miami-2026" element={<MiamiEvent />} />
            <Route path="/miami-2026/gracias" element={<MiamiEventSuccess />} />
            <Route path="/preguntas-frecuentes" element={<FAQPage />} />
            <Route path="/sobre" element={<AboutPage />} />
            <Route path="/quiero-ser-invitado" element={<GuestInquiryPage />} />
            <Route path="/invitados" element={<PastGuestsPage />} />
            <Route path="/vision-2026" element={<Vision2026Page />} />
            <Route path="/cuenta-tu-historia" element={<TellYourStoryPage />} />
            <Route path="/ser-invitado-podcast-latinoamericano" element={<BeAGuestSEOPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  </AuthProvider>
);

export default App;
